/**
 * /earn — Earn Coins hub
 *
 * Live page powered by:
 *   - GET  /api/rewards/streak       (StreakCard)
 *   - POST /api/rewards/daily-login  (StreakCard claim)
 *   - GET  /api/rewards/referral-stats (ReferralProgress)
 *
 * Social actions, content sharing, and ad rewards are scaffolded as
 * "coming soon" placeholders. They light up as their claim APIs land.
 *
 * Mock-data preview lives at /preview/earn-coins for design iteration.
 */

import Link from 'next/link';
import { useAuthStore } from '@/stores/auth.store';
import { StreakCard } from '@/components/rewards/StreakCard';
import ReferralProgress from '@/components/rewards/ReferralProgress';
import RewardsList from '@/components/rewards/RewardsList';
import { Coin } from '@/components/ui/coin';

export default function EarnPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const balance = useAuthStore((s) => s.user?.coins ?? 0);

  if (!isHydrated) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="h-8 bg-surface-2 rounded animate-pulse" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8 text-center space-y-4">
        <h1 className="text-xl font-bold text-cream">Earn Coins</h1>
        <p className="text-sm text-warm-gray/60">Sign in to start earning coins through daily logins, referrals, and more.</p>
        <Link href="/login" className="inline-block px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold font-bold text-sm hover:bg-gold/25 transition-all">
          Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-cream">Earn Coins</h1>
          <p className="text-xs text-warm-gray/50 mt-0.5">Complete actions to earn coins for prop boosts.</p>
        </div>
        <Coin
          amount={balance}
          size="md"
          className="gap-2 bg-surface-2 border border-surface-3 rounded-control px-3 py-2"
          valueClassName="text-sm font-bold text-cream"
        />
      </div>

      {/* Daily Streak — LIVE */}
      <StreakCard />

      {/* Referrals — LIVE */}
      <ReferralProgress />

      {/* Social / Content / Ads / Milestones — fetches live catalog from /api/rewards/available.
          Stubbed rewards have isActive=false and don't render. Admin flips to activate. */}
      <RewardsList />

      {/* Footer link to store */}
      <div className="rounded-lg border border-surface-3/50 bg-surface-2/50 p-3 text-center">
        <p className="text-[11px] text-warm-gray/50">
          Need coins now? <Link href="/store" className="text-gold hover:underline font-semibold">Buy a bundle</Link>
        </p>
      </div>
    </div>
  );
}
